// Shared deterministic movement rules. Used by the server (authoritative) and the client
// (prediction + replay). Both step at CONFIG.physicsRate with the same dt.
import { CONFIG, VehicleSpec } from './data';
import { GameMap, TILE, tileAt, solidFoot, solidCar } from './map';
import { clamp, wrapAngle } from './util';
import { trunkHit, carWaterHit } from './country-plan';

export const PHYS_DT = 1 / CONFIG.physicsRate;
export const STEPS_PER_SNAPSHOT = CONFIG.physicsRate / CONFIG.snapshotRate;

export interface PxRect { x: number; y: number; w: number; h: number }
export interface InputCmd {
  seq: number;
  mx: number; my: number;   // -1..1 move / steer(mx) & throttle(-my)
  ax: number; ay: number;   // aim point (world px)
  fire: boolean;
  hb?: boolean;             // handbrake held (driver only)
}
export interface Pos { x: number; y: number }
export interface VState { x: number; y: number; a: number; vx: number; vy: number }
/** drift: share of the sideways speed a car gains as forward speed while the slide dies away (Drift King perk), 0 = none */
export interface VMods { accel: number; grip: number; turn: number; speed: number; drift?: number }
export const NO_MODS: VMods = { accel: 1, grip: 1, turn: 1, speed: 1 };

function boxHits(m: GameMap, x: number, y: number, r: number, car: boolean, extra?: PxRect[], inTrunk = false): boolean {
  const tx0 = Math.floor((x - r) / TILE), tx1 = Math.floor((x + r - 0.001) / TILE);
  const ty0 = Math.floor((y - r) / TILE), ty1 = Math.floor((y + r - 0.001) / TILE);
  for (let ty = ty0; ty <= ty1; ty++) for (let tx = tx0; tx <= tx1; tx++) {
    const t = tileAt(m, tx, ty);
    if (car ? solidCar(t) && !(m.grass && m.grass[ty * m.w + tx]) : solidFoot(t)) return true; // (the sanctuary's grass is open to cars)
  }
  if (!inTrunk && trunkHit(m, x, y, r)) return true; // tree trunks in the sanctuary forest (country-plan.ts): they stop cars like walls
  if (car && carWaterHit(x, y, r)) return true; // paddies, stream and onsen pool: people wade, vehicles don't
  if (extra) for (const b of extra) if (x + r > b.x && x - r < b.x + b.w && y + r > b.y && y - r < b.y + b.h) return true;
  return false;
}
export const boxBlocked = boxHits;

export function moveBox(m: GameMap, p: Pos, dx: number, dy: number, r: number, car: boolean, extra?: PxRect[]) {
  let hitX = false, hitY = false;
  const inTrunk = trunkHit(m, p.x, p.y, r); // someone already overlapping a trunk (teleport, respawn, a spawned car) can always move out
  const steps = Math.max(1, Math.ceil(Math.max(Math.abs(dx), Math.abs(dy)) / (r * 0.8)));
  const sx = dx / steps, sy = dy / steps;
  for (let i = 0; i < steps; i++) {
    if (!hitX && sx !== 0) { const nx = p.x + sx; if (boxHits(m, nx, p.y, r, car, extra, inTrunk)) hitX = true; else p.x = nx; }
    if (!hitY && sy !== 0) { const ny = p.y + sy; if (boxHits(m, p.x, ny, r, car, extra, inTrunk)) hitY = true; else p.y = ny; }
  }
  return { hitX, hitY };
}

/** `cars`: solid boxes a walker can't enter (footBlockers); the server and the prediction pass the same kind of list. */
export function stepFoot(m: GameMap, p: Pos, mx: number, my: number, speed: number, dt = PHYS_DT, cars?: PxRect[]) {
  let l = Math.hypot(mx, my);
  if (l > 1) { mx /= l; my /= l; l = 1; }
  if (l < 0.05) return;
  moveBox(m, p, mx * speed * dt, my * speed * dt, CONFIG.player.radius, false, cars);
}

/** At or above this speed (px/s) a car shoves a walker aside (room.ts "vehicles vs people"); below it, it is solid. */
export const FOOT_SOLID_CAR_SPEED = 35;
export interface FootCar { x: number; y: number; vx: number; vy: number; r: number }
/** The parked or slow cars near a walker, as boxes stepFoot won't walk into (players walked through parked cars before:
 *  only moving cars pushed them, bug report 2026-10-12). A car the walker already overlaps is left out, so getting out
 *  of a car or one rolling onto you never traps anyone. */
export function footBlockers(p: Pos, cars: Iterable<FootCar>): PxRect[] {
  const out: PxRect[] = [], pr = CONFIG.player.radius;
  for (const c of cars) {
    const dx = Math.abs(c.x - p.x), dy = Math.abs(c.y - p.y);
    if (dx > c.r + 48 || dy > c.r + 48 || Math.hypot(c.vx, c.vy) >= FOOT_SOLID_CAR_SPEED) continue;
    if (dx < c.r + pr && dy < c.r + pr) continue;
    out.push({ x: c.x - c.r, y: c.y - c.r, w: 2 * c.r, h: 2 * c.r });
  }
  return out;
}

/** Handbrake: grip drops and steering bites harder, so the car slides and swings round; it also bleeds speed. */
export const HANDBRAKE = { grip: 0.18, turn: 1.6, drag: 0.9 };
/** Arcade vehicle step. Returns impact speed against static geometry (0 if none). */
export function stepVehicle(m: GameMap, v: VState, throttle: number, steer: number, spec: VehicleSpec, mods: VMods, extra: PxRect[] | undefined, hb = false, dt = PHYS_DT): number {
  const fx = Math.cos(v.a), fy = Math.sin(v.a);
  let fwd = v.vx * fx + v.vy * fy;
  let lat = -v.vx * fy + v.vy * fx;
  const maxF = spec.maxSpeed * mods.speed, maxR = spec.maxSpeed * 0.35;
  if (throttle > 0.05) fwd += (fwd < -5 ? spec.brake : spec.accel * mods.accel) * throttle * dt;
  else if (throttle < -0.05) fwd -= (fwd > 5 ? spec.brake : spec.accel * 0.7 * mods.accel) * -throttle * dt;
  else fwd -= fwd * Math.min(1, 1.1 * dt) + Math.sign(fwd) * Math.min(Math.abs(fwd), 6 * dt);
  if (hb) fwd -= fwd * HANDBRAKE.drag * dt;
  fwd = clamp(fwd - fwd * 0.12 * dt, -maxR, maxF);
  const sp = hb ? Math.hypot(fwd, lat) : Math.abs(fwd); // sliding sideways still lets the car pivot
  const sf = clamp(sp / 45, 0, 1) * (sp > spec.maxSpeed * 0.8 ? 0.85 : 1);
  v.a = wrapAngle(v.a + steer * spec.turn * mods.turn * (hb ? HANDBRAKE.turn : 1) * dt * sf * Math.sign(fwd || 1));
  // lateral slip decays exponentially -> drift that recovers on its own (forgiving)
  const nx = Math.cos(v.a), ny = Math.sin(v.a);
  if (hb) { // momentum stays in world space while the car swings round: that difference is the slide
    const wx = fx * fwd - fy * lat, wy = fy * fwd + fx * lat;
    fwd = wx * nx + wy * ny; lat = -wx * ny + wy * nx;
  }
  const keep = Math.exp(-spec.grip * mods.grip * (hb ? HANDBRAKE.grip : 1) * dt);
  // Drift King: with the handbrake released, the slide that grip kills is turned into forward speed instead of lost
  if (!hb && mods.drift) fwd = clamp(fwd + Math.sign(fwd || 1) * Math.abs(lat) * (1 - keep) * mods.drift, -maxR, maxF * 1.1);
  lat *= keep;
  v.vx = nx * fwd - ny * lat;
  v.vy = ny * fwd + nx * lat;
  const res = moveBox(m, v, v.vx * dt, v.vy * dt, spec.radius, true, extra);
  let impact = 0;
  if (res.hitX) { impact = Math.max(impact, Math.abs(v.vx)); v.vx *= -0.3; v.vy *= 0.8; }
  if (res.hitY) { impact = Math.max(impact, Math.abs(v.vy)); v.vy *= -0.3; v.vx *= 0.8; }
  return impact;
}

/** Sideways speed (px/s): large while the car slides, used for skid marks. */
export const vSlip = (v: VState) => Math.abs(-v.vx * Math.sin(v.a) + v.vy * Math.cos(v.a));
export const vSpeed = (v: VState) => Math.hypot(v.vx, v.vy);
